/**
 * Case Canvas data source — loads a REAL case's projected graph from the backend
 * (GET /api/v1/canvas/{id}/graph) and maps the {nodes, edges} envelope into
 * Cytoscape elements the fusion canvas renders. Also builds the "multi-surface
 * demo overlay" — the synthetic bank/social/company/identity surfaces from the
 * seed — so a (crypto-only) real case can be shown alongside the fused-workbench
 * vision. Isolated: own fetch + token (does not touch the shared api.ts).
 */
import type { ElementDefinition } from 'cytoscape';
import { SEED_NODES, SEED_EDGES, toCyElements } from '../data/fusionSeed';
import { detectChainCandidates, detectChainStrict } from '../utils/chainDetect';
import { handleSessionExpired } from './api';
import type { CaseLinkRow } from '../case-canvas/relatedCases';
import { VISIBILITY } from '../constants/visibility';
import { WALLET_ROLE_LABEL } from '../constants/walletRole';

const API_BASE_URL = (import.meta.env as unknown as Record<string, string>).VITE_API_URL || 'http://localhost:8000/api/v1';

export interface EnvNode {
  id: string;
  module?: string;
  type?: string;
  label?: string;
  risk_score?: number;
  chain?: string | null;
  address?: string | null;
  canonical_id?: string | null;
  attribution?: Record<string, unknown> | null;
  /** level=high cluster nodes */
  collapsed?: boolean;
  /** Declared lane / bundle (spine `attribution.phase` / `attribution.group`). */
  phase?: string | null;
  group?: string | null;
  /** level=high cluster: wallets folded in, and value entering / leaving it per token. */
  member_count?: number;
  amounts_by_token?: Record<string, number>;
  outflow_by_token?: Record<string, number>;
  collapsed_edge_count?: number;
  internal_edge_count?: number;
  /** Matched to a seizure-matrix target of the latest case-worker run. */
  seizure_target?: boolean;
  /** RET01: every edge of this node is an earlier run's (declared at read time; the stylesheet decides the look). */
  dormant?: boolean;
  dormant_since_run?: string | null;
  dormant_since?: string | null;
  /** CANVAS03: officer-assigned private label and count of officer notes. */
  officer_label?: string | null;
  note_count?: number;
}
/** RET01: active and dormant node counts of the projection, with the since-run range of the dormant ones. */
export interface CaseNodeCoverage {
  active_nodes: number;
  dormant_nodes: number;
  dormant_since?: { first_run?: string | null; first_at?: string | null; last_run?: string | null; last_at?: string | null; runs?: number } | null;
  line?: string;
}
export interface EnvEdge {
  id: string; source: string; target: string; edge_type?: string; determinism?: string; value?: number | null;
  token?: string | null;
  token_symbol?: string | null;
  attribution?: Record<string, unknown> | null;
  /** A later case-worker run did not re-derive this edge (FIX38). Declared state; the stylesheet decides the look. */
  stale?: { reason?: string; since_run?: string | null; label?: string; absence?: boolean } | null;
  /** level=high: number of low-level edges merged into this one */
  collapsed_edge_count?: number;
  /** level=high: merged values per token (never summed across tokens, R15) */
  amounts_by_token?: Record<string, number>;
  unknown_token_edge_count?: number;
  merged_tx_hashes?: string[];
  /** level=high: transfers behind a merged edge */
  transfer_count?: number;
}
/** One lane of the macro view, in left-to-right order. */
export interface CasePhase { id: string; label: string; order: number; node_count?: number }
/** A declared (phase, group) bundle with exact per-token totals from the detail rows. */
export interface CaseGroupSummary {
  key: string;
  phase: string | null;
  group: string;
  label?: string;
  phase_label?: string;
  node_ids: string[];
  wallet_count: number;
  amounts_in_by_token: Record<string, number>;
  amounts_out_by_token: Record<string, number>;
  edges_in?: number;
  edges_out?: number;
  internal_edge_count?: number;
  cluster_ids?: string[];
}
/** A seizure-matrix target with its evidence facts (absent facts are null). */
export interface SeizureTargetEnv {
  index: number;
  entity_name?: string | null;
  address?: string | null;
  chain?: string | null;
  nexus_role?: string | null;
  relevance?: string | null;
  priority?: string | null;
  serveable?: boolean | null;
  actionability?: string | null;
  notice_template_key?: string | null;
  notice_state?: string | null;
  statutory_demands?: string[];
  contact?: unknown;
  tx_hash?: string | null;
  timestamp?: string | number | null;
  amount?: number | null;
  token?: string | null;
  value_usd?: number | null;
  evidence_missing?: boolean | null;
  node_id?: string | null;
  [k: string]: unknown;
}
/** low = full on-chain detail; high = bare relay wallets folded into (phase, group) clusters. */
export type CaseGraphLevel = 'low' | 'high';
export interface CaseGraph {
  case_id: string; nodes: EnvNode[]; edges: EnvEdge[];
  counts?: { nodes: number; edges: number; investigations: number; collapsed_clusters?: number; collapsed_members?: number };
  level?: CaseGraphLevel;
  /** OPEN01: present on `?level=spine`: whether the set is complete and how many context nodes were held back. */
  spine?: { complete: boolean; context_count: number; total_nodes: number; total_edges: number };
  phases?: CasePhase[];
  groups?: CaseGroupSummary[];
  seizure_targets?: SeizureTargetEnv[];
  latest_run?: { event_id: string; ts: string | null } | null;
  coverage?: CaseNodeCoverage | null;
}

/** A run reference as the canvas prints it (first 8 characters of the run id). */
export function shortRunRef(run?: string | null): string {
  const s = typeof run === 'string' ? run.trim() : '';
  return s.length > 8 ? s.slice(0, 8) : s;
}

/**
 * The native hover title of a canvas node: the linked case it also appears in (NS-58) and, for a dormant
 * node (RET01), "seen only in earlier runs (since run N)". Empty when the node declares neither.
 */
export function nodeHoverTitle(data: Record<string, unknown>): string {
  const parts: string[] = [];
  if (data.related_case) parts.push(String(data.related_case_label || 'Related case'));
  if (data.dormant === true) parts.push(String(data.dormant_label || dormantNodeLabel(data.dormant_since_run as string | null)));
  return parts.join('\n');
}

/** The edge label of a coverage gap (CANVAS02). */
export const COVERAGE_GAP_LABEL = 'not read this run (coverage gap)';

/** CANVAS02: whether a projected `stale` block is a coverage gap (`not_read`), not an absence. */
export function isCoverageGap(stale?: { reason?: string; absence?: boolean } | null): boolean {
  if (!stale) return false;
  if (typeof stale.absence === 'boolean') return stale.absence === false;
  return stale.reason === 'not_read';
}

/** CANVAS02: the Inspector line of a coverage-gap edge. */
export function coverageGapNote(run?: string | null): string {
  const ref = shortRunRef(run);
  return `${ref ? `not read in run ${ref}` : 'not read in this run'}: a coverage gap, not a withdrawal`;
}

/** RET01: the hover and Inspector line of a dormant node: "seen only in earlier runs (since run N)". */
export function dormantNodeLabel(sinceRun?: string | null): string {
  const ref = shortRunRef(sinceRun);
  return ref ? `seen only in earlier runs (since run ${ref})` : 'seen only in earlier runs';
}

export interface EntityRef {
  module: string;
  type: string;
  canonical_id: string;
  label?: string;
}

export interface PersistEdgeBody {
  source?: EntityRef;
  target?: EntityRef;
  source_entity?: string;
  target_entity?: string;
  edge_type?: string;
  determinism?: string;
  value?: number;
  attribution?: Record<string, unknown>;
}

export const CASE_GRAPH_TIMEOUT_MS = 60_000;
export const CASE_GRAPH_RETRY_DELAYS_MS = [1000, 3000];

export type CaseGraphErrorKind =
  | 'unauthorized' | 'forbidden' | 'not_found' | 'server' | 'network' | 'timeout' | 'http' | 'aborted';

/** Honest, distinct sentence per failure; "not found" is only ever a real 404. */
export function caseGraphErrorMessage(kind: CaseGraphErrorKind, status?: number): string {
  switch (kind) {
    case 'unauthorized': return 'Your session has expired. Signing you out so you can sign in again.';
    case 'forbidden': return 'You do not have access to this case.';
    case 'not_found': return 'This case does not exist or was deleted.';
    case 'server':
    case 'network':
    case 'timeout': return 'The server did not answer. This is usually temporary.';
    case 'aborted': return 'The request was cancelled.';
    default: return `The case graph could not be loaded (HTTP ${status ?? 'error'}).`;
  }
}

export class CaseGraphError extends Error {
  readonly kind: CaseGraphErrorKind;
  readonly status?: number;
  constructor(kind: CaseGraphErrorKind, status?: number) {
    super(caseGraphErrorMessage(kind, status));
    this.name = 'CaseGraphError';
    this.kind = kind;
    this.status = status;
  }
  /** Worth another attempt: the server may simply not have answered yet. */
  get retryable(): boolean {
    return this.kind === 'server' || this.kind === 'network' || this.kind === 'timeout';
  }
}

/**
 * GET /canvas/{id}/graph. `level: 'high'` asks the backend for the macro view
 * (relay wallets collapsed into clusters carrying `attribution.collapsed_members`);
 * the default stays the full low-level projection, so existing callers are unchanged.
 */
export async function fetchCaseGraph(
  caseId: string,
  opts?: { level?: CaseGraphLevel; spine?: boolean; signal?: AbortSignal; timeoutMs?: number },
): Promise<CaseGraph> {
  const token = localStorage.getItem('token') || '';
  // OPEN01: `spine` asks for the role-bearing nodes only (the context set is fetched when the officer opens it).
  const query = opts?.level === 'high' ? '?level=high' : opts?.spine ? '?level=spine' : '';
  // A hung connection must end: one attempt is bounded by timeoutMs (60 s), and the
  // caller's signal cancels it early (case or level changed under a slow response).
  const ctl = new AbortController();
  const onOuterAbort = () => ctl.abort();
  if (opts?.signal?.aborted) ctl.abort();
  else opts?.signal?.addEventListener('abort', onOuterAbort);
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; ctl.abort(); }, opts?.timeoutMs ?? CASE_GRAPH_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/graph${query}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: ctl.signal,
    });
    if (!res.ok) {
      if (res.status === 401) {
        // Same expired-session path as every axios call: clear the session, go to /login?expired=1.
        handleSessionExpired();
        throw new CaseGraphError('unauthorized', 401);
      }
      if (res.status === 403) throw new CaseGraphError('forbidden', 403);
      if (res.status === 404) throw new CaseGraphError('not_found', 404);
      if (res.status >= 500 || res.status === 408 || res.status === 429) throw new CaseGraphError('server', res.status);
      throw new CaseGraphError('http', res.status);
    }
    return await res.json();
  } catch (e) {
    if (e instanceof CaseGraphError) throw e;
    if (opts?.signal?.aborted) throw new CaseGraphError('aborted');
    if (timedOut) throw new CaseGraphError('timeout');
    throw new CaseGraphError('network');
  } finally {
    clearTimeout(timer);
    opts?.signal?.removeEventListener('abort', onOuterAbort);
  }
}

/**
 * Run `attempt` and, for failures that can be transient (server error, no
 * answer, timeout), try again after 1 s and then 3 s. 401, 403 and 404 are
 * answers, not blips: they are never retried. `onRetry` reports each wait so
 * the page can say "the server did not answer, retrying". Used on the first
 * graph load only; a refetch over a good graph makes a single attempt.
 */
export async function retryTransient<T>(
  attempt: () => Promise<T>,
  opts: { signal?: AbortSignal; delaysMs?: number[]; onRetry?: (n: number, of: number, err: CaseGraphError) => void } = {},
): Promise<T> {
  const delays = opts.delaysMs ?? CASE_GRAPH_RETRY_DELAYS_MS;
  for (let n = 0; ; n++) {
    try {
      return await attempt();
    } catch (e) {
      const err = e instanceof CaseGraphError ? e : new CaseGraphError('network');
      if (!err.retryable || n >= delays.length || opts.signal?.aborted) throw err;
      opts.onRetry?.(n + 1, delays.length, err);
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, delays[n]);
        opts.signal?.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
      });
      if (opts.signal?.aborted) throw new CaseGraphError('aborted');
    }
  }
}

/** Day 21 — persist a Fusion subject-hub / derived edge via POST /canvas/{id}/edges. */
export function persistCaseEdge(caseId: string, body: PersistEdgeBody): void {
  if (!caseId) return;
  const token = localStorage.getItem('token') || '';
  fetch(`${API_BASE_URL}/canvas/${caseId}/edges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  }).catch(() => { /* best-effort — canvas still has the live edge */ });
}

export interface CaseEventRow {
  id: string;
  actor_id?: number | null;
  event_type: string;
  payload: Record<string, unknown>;
  ts: string | null;
}

/** GET /canvas/{id}/events — the case Journal, newest first. */
export async function fetchCaseEvents(caseId: string, limit = 50): Promise<CaseEventRow[]> {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/events?limit=${encodeURIComponent(String(limit))}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Failed to load case events (${res.status})`);
  const body = await res.json();
  return Array.isArray(body?.events) ? (body.events as CaseEventRow[]) : [];
}

/**
 * GET /cases/{id}/links — the cases linked to this one (NS-58). Secondary data: any failure (no access to the
 * links route, an older backend, a network error) yields no links and never blocks the canvas.
 */
export async function fetchCaseLinks(caseId: string, signal?: AbortSignal): Promise<CaseLinkRow[]> {
  try {
    const token = localStorage.getItem('token') || '';
    const res = await fetch(`${API_BASE_URL}/cases/${caseId}/links`, {
      headers: { Authorization: `Bearer ${token}` }, signal,
    });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body?.links) ? (body.links as CaseLinkRow[]) : [];
  } catch {
    return [];
  }
}

export interface CanvasJobCreate {
  job_id: string;
  operator: string;
  sse: string;
}

export interface CanvasJobEvent {
  job_id: string;
  seq: number;
  type: string;
  payload: Record<string, unknown>;
  ts?: string;
}

/** Day 22 — create a canvas job (params ride server pending map for the SSE GET). */
export async function createCanvasJob(
  caseId: string,
  operator: string,
  opts?: { target_ref?: string; params?: Record<string, unknown> },
): Promise<CanvasJobCreate> {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(`${API_BASE_URL}/canvas/${caseId}/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      operator,
      target_ref: opts?.target_ref,
      params: opts?.params || {},
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to create canvas job (${res.status})`);
  }
  return res.json();
}

/**
 * Day 22 — consume the job SSE stream until ``done`` / ``error``.
 * Returns the terminal event payload.
 */
export function streamCanvasJob(
  caseId: string,
  jobId: string,
  operator: string,
  onEvent?: (ev: CanvasJobEvent) => void,
): Promise<Record<string, unknown>> {
  const token = localStorage.getItem('token') || '';
  const url =
    `${API_BASE_URL}/canvas/${caseId}/jobs/${jobId}/events`
    + `?operator=${encodeURIComponent(operator)}`
    + `&token=${encodeURIComponent(token)}`;

  return new Promise((resolve, reject) => {
    const es = new EventSource(url);
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      es.close();
      fn();
    };
    es.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data) as CanvasJobEvent;
        onEvent?.(data);
        if (data.type === 'done') {
          finish(() => resolve(data.payload || {}));
        } else if (data.type === 'error') {
          const message = String(data.payload?.message || 'job failed');
          finish(() => reject(new Error(message)));
        }
      } catch (e) {
        finish(() => reject(e instanceof Error ? e : new Error(String(e))));
      }
    };
    es.onerror = () => {
      finish(() => reject(new Error('canvas job SSE failed')));
    };
  });
}

/** Day 22 — UTR-Stitch operator: create job + stream → durable TRACE on GET /graph. */
export async function runUtrStitchJob(
  caseId: string,
  params: { events?: Record<string, unknown>[]; stitch_result?: Record<string, unknown> },
  targetRef?: string,
  onEvent?: (ev: CanvasJobEvent) => void,
): Promise<Record<string, unknown>> {
  const created = await createCanvasJob(caseId, 'utr_stitch', {
    target_ref: targetRef,
    params,
  });
  return streamCanvasJob(caseId, created.job_id, 'utr_stitch', onEvent);
}

/** Day 23 — Off-Ramp Match → COUNTERPARTY on case graph. */
export async function runOfframpMatchJob(
  caseId: string,
  params: { vpa?: string; account?: string; ifsc?: string; phone?: string; name?: string },
  targetRef?: string,
  onEvent?: (ev: CanvasJobEvent) => void,
): Promise<Record<string, unknown>> {
  const created = await createCanvasJob(caseId, 'offramp_match', {
    target_ref: targetRef,
    params,
  });
  return streamCanvasJob(caseId, created.job_id, 'offramp_match', onEvent);
}

/** Day 23 — Auto-Investigate summary promote (subject + endpoints). */
export async function runAutoInvestigateJob(
  caseId: string,
  params: { address: string; chain?: string; endpoints?: Record<string, unknown>[] },
  targetRef?: string,
  onEvent?: (ev: CanvasJobEvent) => void,
): Promise<Record<string, unknown>> {
  const created = await createCanvasJob(caseId, 'auto_investigate', {
    target_ref: targetRef,
    params,
  });
  return streamCanvasJob(caseId, created.job_id, 'auto_investigate', onEvent);
}

/** The six spatial roles of CANVAS_PLACEMENT_LANGUAGE.md §3.3. */
const PLACEMENT_ROLES = new Set(['subject', 'source', 'sink', 'relay', 'shared_relay', 'context']);

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const obj = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;
const present = (v: unknown): boolean => v !== undefined && v !== null && v !== '';

/** First present value among `keys`: the item's top level first, then its attribution. */
function fact(item: Record<string, unknown>, attr: Record<string, unknown>, ...keys: string[]): unknown {
  for (const source of [item, attr]) {
    for (const k of keys) {
      if (present(source[k])) return source[k];
    }
  }
  return undefined;
}

/** Token amount with precision that suits its size: 7,755 · 9.13 · 0.00012345. */
export function formatAmount(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 2 : abs >= 1 ? 4 : 8;
  return value.toLocaleString('en-US', { maximumFractionDigits: digits });
}

/** "7,755 USDT + 9.13 TRX" — per-token totals side by side, never one cross-token sum (R15). */
export function formatAmountsByToken(amounts: Record<string, number> | undefined | null): string {
  if (!amounts) return '';
  return Object.entries(amounts)
    .filter(([, v]) => typeof v === 'number' && Number.isFinite(v))
    .map(([tok, v]) => `${formatAmount(v)} ${tok}`)
    .join(' + ');
}

/** Epoch seconds / epoch ms / ISO string → epoch ms, or null when unparseable. */
export function toEpochMs(ts: unknown): number | null {
  if (ts === undefined || ts === null || ts === '') return null;
  let n: number;
  if (typeof ts === 'number') n = ts;
  else if (typeof ts === 'string' && /^\d+(\.\d+)?$/.test(ts.trim())) n = Number(ts);
  else {
    const parsed = Date.parse(String(ts));
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (!Number.isFinite(n)) return null;
  const ms = n > 1e12 ? n : n * 1000;
  return Number.isNaN(new Date(ms).getTime()) ? null : ms;
}

/** "17:12:24 UTC" (time of day only; the Inspector shows the full date). */
export function formatUtcTime(ts: unknown): string | null {
  const ms = toEpochMs(ts);
  if (ms === null) return null;
  return `${new Date(ms).toISOString().slice(11, 19)} UTC`;
}

/** Full UTC timestamp "2026-01-01 17:12:24 UTC", or null. */
export function formatUtcDateTime(ts: unknown): string | null {
  const ms = toEpochMs(ts);
  if (ms === null) return null;
  return new Date(ms).toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
}

/** "dwell 222s" under an hour; "dwell 3h 42m" under a day; "dwell 3.9d" beyond. */
export function formatDwell(seconds: unknown): string | null {
  const s = typeof seconds === 'string' ? Number(seconds) : seconds;
  if (typeof s !== 'number' || !Number.isFinite(s) || s < 0) return null;
  if (s < 3600) return `dwell ${Math.round(s)}s`;
  if (s < 86400) return `dwell ${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `dwell ${(s / 86400).toFixed(1)}d`;
}

/** Short form of a tx hash for edge labels (the full hash stays on the element). */
export function shortTxHash(txHash?: string, declared?: string): string | undefined {
  if (declared) return declared;
  if (!txHash) return undefined;
  return txHash.length > 10 ? `${txHash.slice(0, 8)}…` : txHash;
}

/** One transfer behind an edge (producers aggregate parallel transfers into `attribution.transfers`). */
export interface EdgeTransfer {
  tx_hash?: string;
  amount?: number;
  token?: string;
  timestamp?: unknown;
}

/** `attribution.transfers` normalised; tokens fall back to the edge's own token. */
export function edgeTransfers(attr: Record<string, unknown> | undefined, edgeToken?: string): EdgeTransfer[] {
  const raw = attr && Array.isArray(attr.transfers) ? (attr.transfers as unknown[]) : [];
  const out: EdgeTransfer[] = [];
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue;
    const r = t as Record<string, unknown>;
    const amount = typeof r.amount === 'number' ? r.amount : (typeof r.value === 'number' ? r.value : undefined);
    const token = (typeof r.token === 'string' && r.token.trim()) ? r.token.trim().toUpperCase()
      : (typeof r.token_symbol === 'string' && r.token_symbol.trim()) ? r.token_symbol.trim().toUpperCase() : edgeToken;
    const hash = typeof r.tx_hash === 'string' && r.tx_hash ? r.tx_hash : (typeof r.hash === 'string' && r.hash ? r.hash : undefined);
    const ts = r.timestamp_iso ?? r.timestamp ?? r.ts;
    out.push({
      ...(hash ? { tx_hash: hash } : {}),
      ...(amount !== undefined && Number.isFinite(amount) ? { amount } : {}),
      ...(token ? { token } : {}),
      ...(ts !== undefined && ts !== null && ts !== '' ? { timestamp: ts } : {}),
    });
  }
  return out;
}

/** Per-token sums over transfers (R15: never across tokens). */
export function sumTransfersByToken(transfers: EdgeTransfer[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of transfers) {
    if (typeof t.amount !== 'number' || !t.token) continue;
    out[t.token] = Math.round(((out[t.token] || 0) + t.amount) * 1e6) / 1e6;
  }
  return out;
}

export interface EdgeFacts {
  value?: number | null;
  token?: string;
  amountsByToken?: Record<string, number>;
  collapsedCount?: number;
  timestamp?: unknown;
  txHash?: string;
  shortHash?: string;
  dwellSeconds?: unknown;
  /** Scored (inferred) edge: the label says so. */
  scored?: boolean;
  /** Text to show when the edge carries no amount (e.g. an identity edge's declared label). */
  fallbackText?: string;
}

/**
 * Edge label: "amount token · HH:MM:SS UTC · short hash · dwell Ns".
 * A merged (macro) edge reads "per-token totals · N transfers" — its members
 * have many times and hashes, so none is printed for it.
 */
export function edgeFactsLabel(f: EdgeFacts): string {
  const body = edgeFactsBody(f);
  if (!f.scored) return body;
  // Scored edges are inferred, never ledger facts: say so on the canvas.
  return /\binferred\b/i.test(body) ? body : (body ? `inferred · ${body}` : 'inferred');
}

function edgeFactsBody(f: EdgeFacts): string {
  const count = f.collapsedCount ?? 0;
  if (count > 1) {
    const amounts = formatAmountsByToken(f.amountsByToken);
    return amounts ? `${amounts} · ${count} transfers` : `${count} transfers`;
  }
  const parts: string[] = [];
  if (typeof f.value === 'number' && f.value) {
    parts.push(f.token ? `${formatAmount(f.value)} ${f.token}` : formatAmount(f.value));
  } else {
    const amounts = formatAmountsByToken(f.amountsByToken);
    if (amounts) parts.push(amounts);
  }
  const time = formatUtcTime(f.timestamp);
  if (time) parts.push(time);
  const hash = shortTxHash(f.txHash, f.shortHash);
  if (hash) parts.push(hash);
  const dwell = formatDwell(f.dwellSeconds);
  if (dwell) parts.push(dwell);
  if (!parts.length && f.fallbackText) parts.push(f.fallbackText);
  return parts.join(' · ');
}

/**
 * Map the backend envelope → fusion canvas elements (data shape matches the seed).
 *
 * The semantic fields the backend declares inside `attribution` are lifted to
 * top-level `data` so the stylesheet, the placement layout and the Inspector can
 * read them (`attribution` itself stays nested for provenance):
 *   nodes: semantic_role, placement_role (one of the six roles), deep_dive,
 *          phase, group, seizure_target,
 *          collapsed_members / member_count / amounts_by_token / outflow_by_token
 *          (level=high clusters)
 *   edges: token_symbol, tx_hash, short_hash, timestamp, dwell_seconds, phase,
 *          group, kind, is_rapid_sweep (only when true), and for merged macro
 *          edges collapsed_edge_count / amounts_by_token / merged_tx_hashes
 * Values are data, never styles: the stylesheet decides presentation (R23).
 */
export function envelopeToElements(nodes: EnvNode[], edges: EnvEdge[]): ElementDefinition[] {
  const ids = new Set(nodes.map((n) => n.id));

  // LINK02: The subject node of a subject_link takes placement role 'subject' only if no seed holds it, else 'context'.
  const subjectLinkSources = new Set<string>();
  for (const e of edges) {
    if ((e.edge_type || '').toLowerCase() === 'subject_link' && e.source) {
      subjectLinkSources.add(e.source);
    }
  }

  const seedHoldsSubject = nodes.some((n) => {
    if (subjectLinkSources.has(n.id)) return false;
    const attr = obj(n.attribution) || {};
    const pr = (str(attr.placement_role) || str((n as unknown as Record<string, unknown>).placement_role))?.toLowerCase();
    return pr === 'subject';
  });

  const ns: ElementDefinition[] = nodes.map((n) => {
    const type = n.type || 'wallet';
    const isWalletAddress = type.toLowerCase() === 'wallet' || type.toLowerCase() === 'address';
    const attr = obj(n.attribution) || {};
    const raw = n as unknown as Record<string, unknown>;
    const attributionChain = str(attr.chain);
    const address = isWalletAddress ? (n.address || n.canonical_id || n.id) : undefined;
    const detectedChain = address && detectChainCandidates(address).length === 1
      ? detectChainStrict(address)
      : undefined;
    const semanticRole = str(attr.semantic_role);
    const rawPlacement = str(attr.placement_role)?.toLowerCase();
    let placementRole = rawPlacement && PLACEMENT_ROLES.has(rawPlacement) ? rawPlacement : '';
    if (subjectLinkSources.has(n.id)) {
      placementRole = seedHoldsSubject ? 'context' : 'subject';
    }
    const deepDive = obj(attr.deep_dive);
    const phase = str(fact(raw, attr, 'phase'))?.toLowerCase();
    const group = str(fact(raw, attr, 'group'));
    const members = Array.isArray(attr.collapsed_members)
      ? (attr.collapsed_members as unknown[]).map(String).filter(Boolean)
      : undefined;
    const memberCount = members
      ? (num(n.member_count) ?? num(attr.member_count) ?? members.length)
      : undefined;
    const amountsIn = obj(n.amounts_by_token) as Record<string, number> | undefined;
    const amountsOut = obj(n.outflow_by_token) as Record<string, number> | undefined;
    const walletRole = str(fact(raw, attr, 'wallet_role'));
    const roleBadge = walletRole && WALLET_ROLE_LABEL[walletRole] ? WALLET_ROLE_LABEL[walletRole] : undefined;
    const systemLabel = n.label || n.id;
    const officerLabel = str(raw.officer_label) || str(n.officer_label);
    const noteCount = num(raw.note_count) ?? num(n.note_count) ?? 0;
    const hasNotes = Boolean(noteCount && noteCount > 0);
    let label = officerLabel || systemLabel;
    if (!officerLabel) {
      if (memberCount !== undefined && !label.includes(String(memberCount))) label = `${label} (${memberCount})`;
      const inText = formatAmountsByToken(amountsIn);
      if (members && inText) label = `${label}\n${inText} in`;
    }
    return {
      data: {
        id: n.id, label, module: n.module || 'crypto', type,
        system_label: systemLabel,
        ...(officerLabel ? { officer_label: officerLabel } : {}),
        has_notes: hasNotes,
        note_count: noteCount,
        risk_score: n.risk_score || 0, size: 4 + Math.round((n.risk_score || 0) * 6), step: 1,
        placement_role: placementRole,
        ...(walletRole ? { wallet_role: walletRole } : {}),
        ...(roleBadge ? { role_badge: roleBadge } : {}),
        ...(semanticRole ? { semantic_role: semanticRole } : {}),
        ...(deepDive ? { deep_dive: deepDive } : {}),
        ...(phase ? { phase } : {}),
        ...(group ? { group } : {}),
        ...(n.seizure_target === true ? { seizure_target: true } : {}),
        // RET01: a dormant node is drawn like a stale edge, through the one opacity preset (R11).
        ...(n.dormant === true ? {
          dormant: true, dormant_since_run: n.dormant_since_run ?? null, dormant_since: n.dormant_since ?? null,
          dormant_label: dormantNodeLabel(n.dormant_since_run), _visibility: VISIBILITY.EARLIER_RUN,
        } : {}),
        ...(members ? { collapsed_members: members, member_count: memberCount } : {}),
        ...(members && amountsIn ? { amounts_by_token: amountsIn } : {}),
        ...(members && amountsOut ? { outflow_by_token: amountsOut } : {}),
        ...(members && num(n.collapsed_edge_count) !== undefined ? { collapsed_edge_count: n.collapsed_edge_count } : {}),
        chain: n.chain || attributionChain || detectedChain || undefined,
        address,
        canonical_id: n.canonical_id || undefined,
        attribution: n.attribution || undefined,
      },
    };
  });
  const es: ElementDefinition[] = edges
    .filter((e) => ids.has(e.source) && ids.has(e.target))
    .map((e) => {
      const attr = obj(e.attribution) || {};
      const raw = e as unknown as Record<string, unknown>;
      const value = typeof e.value === 'number' ? e.value : 0;
      // Upper-cased like the backend's _edge_token, so per-token sums key on one spelling.
      const token = (str(e.token_symbol) || str(e.token) || str(attr.token_symbol) || str(attr.token) || str(attr.unit))?.toUpperCase();
      const txHash = str(fact(raw, attr, 'tx_hash'));
      const shortHash = str(fact(raw, attr, 'short_hash'));
      const timestamp = fact(raw, attr, 'timestamp_iso', 'timestamp', 'ts');
      const dwell = fact(raw, attr, 'dwell_seconds', 'latency_seconds');
      const phase = str(fact(raw, attr, 'phase'))?.toLowerCase();
      const group = str(fact(raw, attr, 'group'));
      const kind = str(attr.kind);
      const rapidSweep = attr.is_rapid_sweep === true || kind === 'rapid_sweep';
      const transfers = edgeTransfers(attr, token);
      const mergedCount = typeof e.collapsed_edge_count === 'number' ? e.collapsed_edge_count : undefined;
      // An edge standing for several transfers (macro merge, or producer-side
      // aggregation of parallel transfers) is labelled by per-token totals and
      // its transfer count, never by one member's hash or time.
      const transferCount = Math.max(
        mergedCount ?? 0, transfers.length,
        typeof e.transfer_count === 'number' ? e.transfer_count : 0,
        num(attr.tx_count) ?? 0,
      );
      const collapsedCount = mergedCount;
      const declaredAmounts = (obj(e.amounts_by_token) || obj(attr.amounts_by_token)) as Record<string, number> | undefined;
      const amounts = declaredAmounts && Object.keys(declaredAmounts).length
        ? declaredAmounts
        : (transfers.length > 1 ? sumTransfersByToken(transfers) : undefined);
      const merged = transferCount > 1;
      const only = transfers.length === 1 ? transfers[0] : undefined;
      const scored = (e.determinism || '') === 'scored';
      const label = edgeFactsLabel({
        value: merged ? undefined : value,
        token,
        amountsByToken: amounts,
        collapsedCount: merged ? transferCount : collapsedCount,
        timestamp: merged ? undefined : (timestamp ?? only?.timestamp),
        txHash: merged ? undefined : (txHash ?? only?.tx_hash),
        shortHash: merged ? undefined : shortHash,
        dwellSeconds: merged ? undefined : dwell,
        scored,
        fallbackText: str(attr.label),
      });
      // CANVAS02: a `not_read` mark (the run read neither wallet) is a coverage gap of one run, not an absence.
      // The projection says so with `absence: false`; an older payload without the field falls back on the reason.
      const gap = isCoverageGap(e.stale);
      const staleLabel = gap ? COVERAGE_GAP_LABEL : (e.stale ? (e.stale.label || 'earlier run, not re-derived') : '');
      const edgeType = e.edge_type || 'trace';
      const isSubjectLink = edgeType === 'subject_link';
      const grade = str(fact(raw, attr, 'grade'));
      const retracted = fact(raw, attr, 'retracted') === true || attr.retracted === true;
      return {
        data: {
          id: e.id, source: e.source, target: e.target, edge_type: edgeType,
          determinism: e.determinism || (isSubjectLink && grade === 'onchain' ? 'deterministic' : 'scored'),
          value, step: 1,
          _edgeLabel: isSubjectLink
            ? (retracted ? 'subject link (retracted)' : `subject link (${grade || 'judgement'})`)
            : (staleLabel ? (label ? `${label}\n(${staleLabel})` : `(${staleLabel})`) : label),
          ...(isSubjectLink ? {
            grade: grade || 'judgement',
            retracted: Boolean(retracted),
            ...(retracted ? { _visibility: VISIBILITY.EARLIER_RUN } : {}),
          } : {}),
          ...(token ? { token_symbol: token } : {}),
          ...(txHash ? { tx_hash: txHash } : {}),
          ...(shortHash ? { short_hash: shortHash } : {}),
          ...(present(timestamp) ? { timestamp } : {}),
          ...(present(dwell) ? { dwell_seconds: dwell } : {}),
          ...(phase ? { phase } : {}),
          ...(group ? { group } : {}),
          ...(kind ? { kind } : {}),
          ...(rapidSweep ? { is_rapid_sweep: true } : {}),
          ...(gap
            ? {
              coverage_gap: true, stale_reason: 'not_read', _visibility: VISIBILITY.SECONDARY,
              ...(e.stale?.since_run ? { coverage_gap_run: e.stale.since_run } : {}),
            }
            : (e.stale ? { stale: true, stale_reason: e.stale.reason || 'not_rederived', _visibility: VISIBILITY.EARLIER_RUN } : {})),
          ...(collapsedCount ? { collapsed_edge_count: collapsedCount } : {}),
          ...(amounts && Object.keys(amounts).length ? { amounts_by_token: amounts } : {}),
          ...(transfers.length ? { transfers } : {}),
          ...(transferCount > 1 ? { transfer_count: transferCount } : {}),
          ...(Array.isArray(e.merged_tx_hashes) && e.merged_tx_hashes.length ? { merged_tx_hashes: e.merged_tx_hashes } : {}),
          ...(e.attribution ? { attribution: e.attribution } : {}),
        },
      };
    });
  return [...ns, ...es];
}

const DEMO_MODULES = new Set(['bank', 'social', 'company', 'network', 'shared']);

/**
 * The synthetic non-crypto surfaces from the seed, forced to step 1 (always visible).
 * Annotation notes and the hand-drawn `box:*` group containers are demo staging,
 * not surfaces, so they never enter a real case's overlay (and children lose the
 * dangling parent reference).
 */
export function demoSurfaceElements(): ElementDefinition[] {
  const isStaging = (n: { id: string; type: string }) => n.type === 'annotation' || n.id.startsWith('box:');
  const nodes = SEED_NODES.filter((n) => DEMO_MODULES.has(n.module) && !isStaging(n));
  const ids = new Set(nodes.map((n) => n.id));
  const edges = SEED_EDGES.filter((e) => ids.has(e.source) && ids.has(e.target));
  return toCyElements(nodes, edges).map((el) => {
    const data: Record<string, unknown> = { ...(el.data as Record<string, unknown>), step: 1 };
    if (typeof data.parent === 'string' && !ids.has(data.parent)) delete data.parent;
    return { data };
  });
}

/** Real case elements + the demo surfaces, with one clearly-illustrative link into the case. */
export function mergeWithDemo(real: ElementDefinition[], linkToRealId?: string): ElementDefinition[] {
  const merged = [...real, ...demoSurfaceElements()];
  if (linkToRealId) {
    merged.push({
      data: {
        id: `demo-link:${linkToRealId}`, source: 'id:rkumar', target: linkToRealId,
        edge_type: 'cross_link', determinism: 'scored', value: 0, step: 1, _edgeLabel: 'illustrative',
      },
    });
  }
  return merged;
}
